import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Shield, 
  CheckCircle, 
  Star, 
  Loader2, 
  CreditCard,
  Calendar,
  AlertCircle,
  ArrowRight,
  BookOpen,
  Briefcase,
  FileText,
  MessageSquare,
  Sparkles,
  Award,
  AlertTriangle,
  Clock,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Membership = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [canceling, setCanceling] = useState(false);

  // Check if returning from Stripe checkout
  const sessionId = searchParams.get('session_id');
  const paymentType = searchParams.get('type');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      fetchSubscriptionStatus();
    }
  }, [sessionId, token]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      setLoading(false);
      navigate('/membership', { replace: true });
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/subscription/checkout-status/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        toast.success(paymentType === 'initiation' 
          ? 'Initiation payment successful! Now activate your monthly subscription.'
          : 'Welcome to Atlas! Your membership is now active.');
        navigate('/membership', { replace: true });
        fetchSubscriptionStatus();
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        navigate('/membership', { replace: true });
        setLoading(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  };

  const initiatePayment = async (packageType) => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API_URL}/api/subscription/create-checkout`, {
        package_type: packageType,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setProcessingPayment(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.')) {
      return;
    }

    setCanceling(true);
    try {
      const response = await axios.post(`${API_URL}/api/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subscription canceled. Access continues until ' + new Date(response.data.access_until).toLocaleDateString());
      fetchSubscriptionStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      </DashboardLayout>
    );
  }

  const isFullyActive = subscription?.is_active && subscription?.has_initiation;
  const needsInitiation = !subscription?.has_initiation;
  const needsSubscription = subscription?.has_initiation && !subscription?.is_active;

  // Membership benefits
  const benefits = [
    { icon: BookOpen, text: 'Complete Learning Suite (8 Modules)' },
    { icon: MessageSquare, text: 'AI Career & Benefits Chatbots' },
    { icon: FileText, text: 'Document Generator (Resume, Cover Letter, etc.)' },
    { icon: Briefcase, text: 'Veteran-Optimized Job Search' },
    { icon: Award, text: 'ATS Resume Scoring' },
    { icon: Sparkles, text: 'AI-Powered Job Matching' }
  ];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="membership-page">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold text-navy-900 dark:text-white mb-2">
            Atlas Membership
          </h1>
          <p className="text-navy-600 dark:text-navy-300">
            Your career transformation starts here
          </p>
        </div>

        {/* Payment Alert Banner - Show when past_due or suspended */}
        {(subscription?.payment_status === 'past_due' || subscription?.payment_status === 'suspended') && (
          <Card className={`mb-8 ${
            subscription?.payment_status === 'suspended' 
              ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/30' 
              : 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30'
          }`}>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    subscription?.payment_status === 'suspended'
                      ? 'bg-red-100 dark:bg-red-900/50'
                      : 'bg-amber-100 dark:bg-amber-900/50'
                  }`}>
                    <AlertTriangle className={`w-7 h-7 ${
                      subscription?.payment_status === 'suspended' 
                        ? 'text-red-600 dark:text-red-400' 
                        : 'text-amber-600 dark:text-amber-400'
                    }`} />
                  </div>
                  <div>
                    <h3 className={`font-heading font-semibold ${
                      subscription?.payment_status === 'suspended'
                        ? 'text-red-800 dark:text-red-200'
                        : 'text-amber-800 dark:text-amber-200'
                    }`}>
                      {subscription?.payment_status === 'suspended' 
                        ? 'Membership Access Suspended' 
                        : 'Payment Failed - Action Required'}
                    </h3>
                    <p className={`text-sm mt-1 ${
                      subscription?.payment_status === 'suspended'
                        ? 'text-red-700 dark:text-red-300'
                        : 'text-amber-700 dark:text-amber-300'
                    }`}>
                      {subscription?.payment_status === 'suspended' 
                        ? 'Your membership is inactive due to a billing issue. Update your payment method to restore access.'
                        : `We couldn't process your payment. Please update your payment method to keep your membership active.`}
                    </p>
                    {subscription?.payment_status === 'past_due' && subscription?.grace_period_end && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-amber-600 dark:text-amber-400">
                        <Clock className="w-4 h-4" />
                        <span>
                          Grace period ends: {new Date(subscription.grace_period_end).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {subscription?.payment_failure_reason && (
                      <p className="text-sm mt-2 text-navy-600 dark:text-navy-400">
                        Reason: {subscription.payment_failure_reason}
                      </p>
                    )}
                  </div>
                </div>
                <Button 
                  className={`flex-shrink-0 ${
                    subscription?.payment_status === 'suspended'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-amber-600 hover:bg-amber-700'
                  }`}
                  onClick={() => initiatePayment('monthly')}
                  disabled={processingPayment}
                  data-testid="update-payment-btn"
                >
                  {processingPayment ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Update Payment Method
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Membership Card */}
        {isFullyActive && (
          <Card className="border-emerald-200 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/30 dark:to-navy-800 mb-8">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/50 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <Badge className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 mb-2">
                      Active Membership
                    </Badge>
                    <h2 className="font-heading text-xl font-semibold text-navy-900 dark:text-white">
                      Atlas Career Solutions
                    </h2>
                    <p className="text-navy-600 dark:text-navy-300">$10/month • Cancel anytime</p>
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-white dark:bg-navy-700/50 rounded-lg border border-navy-100 dark:border-navy-600">
                  <div className="flex items-center gap-2 text-navy-500 dark:text-navy-400 text-sm mb-1">
                    <CreditCard className="w-4 h-4" />
                    Status
                  </div>
                  <p className="font-semibold text-navy-900 dark:text-white capitalize">
                    {subscription?.subscription_status === 'canceled' ? 'Canceled (Active until period end)' : 'Active'}
                  </p>
                </div>
                <div className="p-4 bg-white dark:bg-navy-700/50 rounded-lg border border-navy-100 dark:border-navy-600">
                  <div className="flex items-center gap-2 text-navy-500 dark:text-navy-400 text-sm mb-1">
                    <Calendar className="w-4 h-4" />
                    {subscription?.subscription_status === 'canceled' ? 'Access Until' : 'Next Billing'}
                  </div>
                  <p className="font-semibold text-navy-900 dark:text-white">
                    {subscription?.next_billing_date 
                      ? new Date(subscription.next_billing_date).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
                <div className={`p-4 rounded-lg border ${
                  subscription?.payment_status === 'past_due' 
                    ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800'
                    : subscription?.payment_status === 'suspended'
                    ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800'
                    : 'bg-white dark:bg-navy-700/50 border-navy-100 dark:border-navy-600'
                }`}>
                  <div className="flex items-center gap-2 text-navy-500 dark:text-navy-400 text-sm mb-1">
                    <AlertCircle className="w-4 h-4" />
                    Payment Status
                  </div>
                  <p className={`font-semibold capitalize ${
                    subscription?.payment_status === 'past_due' 
                      ? 'text-amber-600 dark:text-amber-400'
                      : subscription?.payment_status === 'suspended'
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-emerald-600 dark:text-emerald-400'
                  }`}>
                    {subscription?.payment_status === 'past_due' 
                      ? 'Past Due' 
                      : subscription?.payment_status === 'suspended'
                      ? 'Suspended'
                      : 'Active'}
                  </p>
                </div>
              </div>

              {subscription?.subscription_status !== 'canceled' && (
                <div className="border-t border-navy-100 dark:border-navy-600 pt-4">
                  <p className="text-sm text-navy-500 dark:text-navy-400 mb-3">
                    You can cancel anytime. Your access remains active until the end of your current billing cycle.
                  </p>
                  <Button 
                    variant="outline" 
                    className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/30"
                    onClick={cancelSubscription}
                    disabled={canceling}
                  >
                    {canceling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Cancel Subscription
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pricing Section - Show when not fully active */}
        {!isFullyActive && (
          <div className="mb-8">
            {/* Main Pricing Card */}
            <Card className="border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-navy-800 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-600 to-amber-500 text-white p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Shield className="w-8 h-8" />
                  <h2 className="font-heading text-2xl font-bold">Start Your Atlas Career Journey</h2>
                </div>
                <p className="text-amber-100">
                  Begin with a one-time $50 initiation session to unlock full access to the Atlas platform.
                </p>
              </div>

              <CardContent className="p-6">
                {/* Pricing Breakdown */}
                <div className="mb-6">
                  <h3 className="font-heading font-semibold text-navy-900 dark:text-white mb-4">
                    Pricing Breakdown
                  </h3>
                  <div className="space-y-3">
                    <div className={`flex items-center justify-between p-4 rounded-lg border ${
                      needsInitiation 
                        ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30' 
                        : 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30'
                    }`}>
                      <div className="flex items-center gap-3">
                        {subscription?.has_initiation ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-amber-500" />
                        )}
                        <div>
                          <p className="font-medium text-navy-900 dark:text-white">One-time Initiation Session</p>
                          <p className="text-sm text-navy-500 dark:text-navy-400">Unlock your Atlas membership</p>
                        </div>
                      </div>
                      <span className="font-bold text-navy-900 dark:text-white">$50</span>
                    </div>

                    <div className={`flex items-center justify-between p-4 rounded-lg border ${
                      subscription?.is_active 
                        ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30' 
                        : 'border-navy-100 dark:border-navy-700'
                    }`}>
                      <div className="flex items-center gap-3">
                        {subscription?.is_active ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-navy-300 dark:border-navy-600" />
                        )}
                        <div>
                          <p className="font-medium text-navy-900 dark:text-white">Ongoing Monthly Access</p>
                          <p className="text-sm text-navy-500 dark:text-navy-400">Full platform access</p>
                        </div>
                      </div>
                      <span className="font-bold text-navy-900 dark:text-white">$10/month</span>
                    </div>

                    <p className="text-sm text-navy-500 dark:text-navy-400 text-center pt-2">
                      Cancel anytime.
                    </p>
                  </div>
                </div>

                {/* CTA Button */}
                {needsInitiation && (
                  <Button 
                    className="w-full h-14 text-lg bg-amber-600 hover:bg-amber-700"
                    onClick={() => initiatePayment('initiation')}
                    disabled={processingPayment}
                    data-testid="initiation-payment-btn"
                  >
                    {processingPayment ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Pay $50 & Activate Membership
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                )}

                {needsSubscription && (
                  <div>
                    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg mb-4 border border-emerald-200 dark:border-emerald-800">
                      <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Initiation Complete!</span>
                      </div>
                      <p className="text-sm text-navy-600 dark:text-navy-300 mt-1">
                        Now activate your monthly subscription to access all features.
                      </p>
                    </div>
                    <Button 
                      className="w-full h-14 text-lg bg-amber-600 hover:bg-amber-700"
                      onClick={() => initiatePayment('monthly')}
                      disabled={processingPayment}
                      data-testid="subscription-payment-btn"
                    >
                      {processingPayment ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Start $10/month Subscription
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Benefits Grid */}
        <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
              <Star className="w-5 h-5 text-amber-600" />
              What's Included
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-lg hover:bg-navy-50 dark:hover:bg-navy-700/50 transition-colors">
                  <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/50 rounded-lg flex items-center justify-center">
                    <benefit.icon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <span className="text-navy-700 dark:text-navy-200">{benefit.text}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Access Gate Notice - Only show if not active */}
        {!isFullyActive && (
          <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-navy-900 dark:text-white">Membership Required</p>
                <p className="text-sm text-navy-600 dark:text-navy-300">
                  Complete your membership activation to access the Learning Suite, AI Chatbots, Document Generator, and more.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Membership;
